import type { PackVersion } from '@planuze/admin-contracts';
import {
  Button,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { PackageIcon } from 'lucide-react';
import { useAdminLocale } from '../../content/locale-provider.js';
import { usePacksCatalogContent } from '../../content/sections/packs-catalog.js';
import { fmtDateTime } from '../../lib/format.js';
import { versionStatusLabel, versionStatusVariant } from './pack-catalog-labels.js';

interface Props {
  readonly versions: readonly PackVersion[];
  /** Takedown é destrutivo → só super_admin vê a coluna de ações (ADR-0464). */
  readonly canTakedown: boolean;
  /** Versão cujo takedown está em flight — loading só na linha certa. */
  readonly pendingVersion: string | null;
  readonly isTakingDown: boolean;
  readonly onTakedown: (version: PackVersion) => void;
}

/* Aba Versões do detalhe do pack (Fase 2, ADR-0703+): tabela do version-index com status de
   ciclo de vida + takedown de staff por versão (super_admin). Sobre o primitive `Table` de
   @planuze/ui (ADR-0118). `revoked` já não pode ser retirado de novo — sem botão. */
export function PackVersionsTab({
  versions,
  canTakedown,
  pendingVersion,
  isTakingDown,
  onTakedown,
}: Props) {
  const { locale } = useAdminLocale();
  const copy = usePacksCatalogContent().versionsTab;
  if (versions.length === 0) {
    return (
      <EmptyState
        icon={<PackageIcon className="size-6" />}
        title={copy.emptyTitle}
        description={copy.emptyDescription}
      />
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{copy.columnVersion}</TableHead>
          <TableHead>{copy.columnStatus}</TableHead>
          <TableHead>{copy.columnPublishedAt}</TableHead>
          <TableHead>{copy.columnPublisher}</TableHead>
          {canTakedown && <TableHead>{copy.columnActions}</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {versions.map((version) => (
          <TableRow key={version.version}>
            <TableCell>
              <span className="font-mono text-sm text-foreground">v{version.version}</span>
            </TableCell>
            <TableCell>
              <StatusBadge variant={versionStatusVariant(version.status)}>
                {versionStatusLabel(version.status, locale)}
              </StatusBadge>
            </TableCell>
            <TableCell className="text-muted-foreground">
              {fmtDateTime(version.publishedAt)}
            </TableCell>
            <TableCell className="font-mono text-xs text-muted-foreground">
              {version.publishedBy ?? copy.firstParty}
            </TableCell>
            {canTakedown && (
              <TableCell>
                {version.status === 'revoked' ? (
                  <span className="text-xs text-muted-foreground">—</span>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={isTakingDown && pendingVersion === version.version}
                    onClick={() => onTakedown(version)}
                  >
                    {copy.takedown}
                  </Button>
                )}
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
